import { Component, ReactNode } from 'react';

interface Props { children: ReactNode; fallback?: ReactNode }
interface State { hasError: boolean }

const RELOAD_KEY = 'chunk-reload-attempt';

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = (error as Error)?.message ?? String(error);
  const name = (error as Error)?.name ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

/**
 * Catches failed dynamic imports (stale chunk hashes after a new deploy).
 * Auto-reloads the page once; if it still fails, shows fallback so the user
 * isn't stuck on a blank/crashed screen.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    if (isChunkLoadError(error)) {
      try {
        const attempted = sessionStorage.getItem(RELOAD_KEY);
        if (!attempted) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
          return { hasError: false };
        }
      } catch {
        window.location.reload();
        return { hasError: false };
      }
    }
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    if (import.meta.env.DEV) console.error('[ChunkErrorBoundary]', error);
  }

  componentDidMount() {
    // Successful render after a previous reload — clear the flag.
    try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* noop */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
            <p className="font-heading text-xl text-foreground">Something went wrong loading this page.</p>
            <button
              onClick={() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch {} window.location.reload(); }}
              className="rounded-full border border-foreground px-6 py-3 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
            >
              Reload
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}